import type { SubjectFieldFilter, SubjectFieldSort } from '@Application/Subjects/Queries/FetchSubjects';
import AbstractFilterView, { type AbstractFilterViewArgs } from '@UI/Helpers/Filters/AbstractFilterView';

export type SubjectFilterViewArgs = AbstractFilterViewArgs & {
	readonly typeId: number;
	readonly search?: string;
	readonly fieldFilters?: readonly SubjectFieldFilter[];
	readonly fieldSort?: SubjectFieldSort | null;
	readonly includeArchived?: boolean;
};

/**
 * The criteria of the dossier list of one type: the type itself, the title search, the sheet field
 * filters (several values of one field widen the match), the sort on a sheet field (`null` is newest
 * creation date first), whether archived dossiers are listed too, and the page.
 */
class SubjectFilterView extends AbstractFilterView<SubjectFilterViewArgs> {
	readonly typeId: number;
	readonly search: string;
	readonly fieldFilters: readonly SubjectFieldFilter[];
	readonly fieldSort: SubjectFieldSort | null;
	readonly includeArchived: boolean;

	constructor(args: SubjectFilterViewArgs) {
		super(args);

		this.typeId = args.typeId;
		this.search = args.search ?? '';
		this.fieldFilters = args.fieldFilters ?? [];
		this.fieldSort = args.fieldSort ?? null;
		this.includeArchived = args.includeArchived ?? false;

		Object.freeze(this);
	}

	static createDefault(typeId: number): SubjectFilterView {
		return new SubjectFilterView({ typeId });
	}

	/** Number of criteria that narrow the results: the search counts, each filtered field counts once, the sort and the archived toggle never
	 * (the toggle sits beside the search, outside the filter panel). */
	get activeFilterCount(): number {
		const filteredKeys = new Set(this.fieldFilters.map((filter) => filter.key));

		return filteredKeys.size + (this.search.length > 0 ? 1 : 0);
	}

	setIncludeArchived(includeArchived: boolean): this {
		return this.cloneWith({ includeArchived, page: 0 });
	}

	valuesOf(key: string): readonly string[] {
		return this.fieldFilters.filter((filter) => filter.key === key).map((filter) => filter.value);
	}

	setSearch(search: string): this {
		return this.cloneWith({ search, page: 0 });
	}

	/** Replaces the values filtered on one field; an empty selection removes the filter. */
	setFieldValues(key: string, values: readonly string[]): this {
		const otherFilters = this.fieldFilters.filter((filter) => filter.key !== key);
		const fieldFilters = [...otherFilters, ...values.map((value) => ({ key, value }))];

		return this.cloneWith({ fieldFilters, page: 0 });
	}

	/** `null` sorts by creation date; the chosen direction is kept (ascending when none was chosen yet). */
	setSortKey(key: string | null): this {
		return this.cloneWith({
			fieldSort: { key, direction: this.fieldSort?.direction ?? (key === null ? 'DESC' : 'ASC') },
			page: 0,
		});
	}

	setSortDirection(direction: SubjectFieldSort['direction']): this {
		return this.cloneWith({ fieldSort: { key: this.fieldSort?.key ?? null, direction }, page: 0 });
	}

	hasActiveFilters(): boolean {
		return this.activeFilterCount > 0 || this.includeArchived;
	}
}

export default SubjectFilterView;
